import { defineStore } from 'pinia';
import { ref } from 'vue';
import { AppCheckUpdate } from "@/api/app.js"
import { GetLanguageData } from "@/api/base.js"
import { showToast } from "@/utils/utils.js"
import { appSystemInfo } from "../app.config.js";
import storage from "@/utils/storage.js";

export const useSystemStore = defineStore('system', () => {
	const systemInfo = uni.getSystemInfoSync();
	const statusBarHeight = systemInfo.statusBarHeight;
	let navbarHeight;
	let miniProgramMenuSafeArea;
	// #ifdef MP
	{
		const { top, height, left } = uni.getMenuButtonBoundingClientRect();
		navbarHeight = height + (top - statusBarHeight) * 2;
		miniProgramMenuSafeArea = systemInfo.windowWidth - left;
	}
	// #endif
	// #ifndef MP
	{
		navbarHeight = 44;
		miniProgramMenuSafeArea = 0;
	}
	// #endif
	const languageList = ref([]);
	const isLanguageListLoaded = ref(false);
	let languageListPromise = null;

	function getFrontendLanguageKey(key) {
		const backendToFrontendLanguageKey = {
			zh_CN: 'zh-Hans',
			zh_TW: 'zh-Hant',
			en_US: 'en'
		};
		return backendToFrontendLanguageKey[key] || key;
	}

	function formatLanguageList(res) {
		const data = typeof res === 'string' ? JSON.parse(res) : res;
		if (Array.isArray(data)) {
			return data.map(item => ({
				id: getFrontendLanguageKey(item.id ?? item.key ?? item.code),
				name: item.name ?? item.label ?? item.value
			}));
		}
		if (data && typeof data === 'object') {
			return Object.keys(data).map(key => ({
				id: getFrontendLanguageKey(key),
				name: data[key]
			}));
		}
		return [];
	}

	function getLanguageList(force = false) {
		if (!force && isLanguageListLoaded.value) return Promise.resolve(languageList.value);
		if (!force && languageListPromise) return languageListPromise;
		languageListPromise = GetLanguageData('language').then(res => {
			languageList.value = formatLanguageList(res);
			isLanguageListLoaded.value = true;
			return languageList.value;
		}).catch(() => {
			return languageList.value;
		}).finally(() => {
			languageListPromise = null;
		});
		return languageListPromise;
	}

	return { 
		systemInfo, 
		statusBarHeight, 
		navbarHeight, 
		miniProgramMenuSafeArea,
		languageList,
		isLanguageListLoaded,
		getLanguageList,
		getFrontendLanguageKey
	};
});

export const useMiniUpdate = defineStore('miniUpdate', {
	actions:{
		onUpdate(){
			const updateManager = uni.getUpdateManager();
			
			updateManager.onCheckForUpdate(function (res) {
			  // 请求完新版本信息的回调
			  console.log(res.hasUpdate);
			});
			
			updateManager.onUpdateReady(function (res) {
			  uni.showModal({
			    title: '更新提示',
			    content: '新版本已经准备好，是否重启应用？',
			    success(res) {
			      if (res.confirm) {
			        // 新的版本已经下载好，调用 applyUpdate 应用新版本并重启
			        updateManager.applyUpdate();
			      }
			    }
			  });
			});
			
			updateManager.onUpdateFailed(function (res) {
			  // 新的版本下载失败
			});
		}
	}
})

export const useAppUpdateStore = defineStore('appUpdate', {
	state: () => ({
		updateInfo: null, //更新信息
		isUpdate: false, //是否有更新
		isForce: false, //是否强制更新
		isHot: false,  //是否热更新
		isPackage: false, //是否整包更新
		isDownload: false, //是否下载
		downloadProgress: 0, //下载进度
		isInstall: false, // 是否安装
		isCancel: false, // 是否忽略安装
		version: appSystemInfo.version, // 当前App版本号
	}),
	actions: {
		// 检查更新
		onCheckForUpdate(){			
			return new Promise((resolve, reject) => {
				try{
					plus.runtime.getProperty(plus.runtime.appid, async widgetInfo => {
					    // console.log(widgetInfo)
						this.version = widgetInfo.version;
						this.updateInfo = await AppCheckUpdate(plus.os.name);
						this.isCancel = (storage.get('updateVersion') === this.updateInfo.appVersion);
						
						//当前版本
						const appVersions = widgetInfo.version.split('.').map(item => +item);
						
						//服务器版本
						const serverVersions = this.updateInfo.appVersion.split('.').map(item => +item);
						
						// 大版本更新
						if(serverVersions[0] > appVersions[0] || serverVersions[1] > appVersions[1]){
							this.isUpdate = true;
							this.isForce = this.updateInfo.updateType === 1;
							this.isPackage = true;
							return;
						}
						
						// 热更新
						if(serverVersions[2] > appVersions[2] && serverVersions[0] === appVersions[0] && serverVersions[1] === appVersions[1]){
							this.isUpdate = true;
							this.isForce = true;
							this.isHot = true;
						}
						
						resolve();
					});
				}catch(err){
					//TODO handle the exception
					reject(err);
				}
			})
		},
		// 开始更新
		onUpdate(){
			if(plus.os.name === "iOS" && this.isPackage){
				plus.runtime.openURL(this.updateInfo.downloadPage)
				return;
			}
			
			try{
				this.isDownload = true;
				const downloadTask =  uni.downloadFile({
					url: this.isPackage ? this.updateInfo.downloadLink : this.updateInfo.wgtDownloadLink,  
					success: (downloadResult) => {
						if (downloadResult.statusCode === 200) {
							this.isInstall = true;
							plus.runtime.install(downloadResult.tempFilePath, {}, function() {
								plus.runtime.restart();
							}, function(error) {
								plus.nativeUI.alert(error.message, function(){
									plus.runtime.restart(); 
								}, '安装失败', '确定');
							});  
						}
				   },
				   fail(error){
						plus.nativeUI.alert('下载失败', function(){
							plus.runtime.restart(); 
						}, '', '确定');
				   },
				   complete(e){
					   console.log(e);
					   if(e.statusCode !== 200){
						   plus.nativeUI.alert('下载失败', function(){
						   	plus.runtime.restart(); 
						   }, '', '确定');
					   }
				   }
				});  
				
				downloadTask.onProgressUpdate((res) => {
					this.downloadProgress = res.progress;
				});
			}catch(e){
				//TODO handle the exception
				console.log(e);
			}
		}
	}
});
